// Assessment page data: query options of the detail, the result rows, the impact of a newer grid-code version and one topic, a reviewed topic written into the topic and row caches, and invalidation of the assessment reads (detail, rows, topics, the assessments list and the capability documents, whose "used by" changes) after a review, approval, reopening, run, creation or deletion.
import type { Query, QueryClient } from "@tanstack/react-query";

import type { TopicResultDetail, TopicResultRow } from "@/api/__generated__";
import {
  assessmentsGetAssessmentOptions,
  assessmentsGetTopicResultOptions,
  assessmentsGetVersionImpactOptions,
  assessmentsListTopicResultsOptions,
} from "@/api/__generated__/@tanstack/react-query.gen";

export const RUN_POLL_MS = 5_000;

export function detailOptions(assessmentUid: string) {
  return assessmentsGetAssessmentOptions({ path: { assessment_uid: assessmentUid } });
}

export function resultsOptions(assessmentUid: string) {
  return assessmentsListTopicResultsOptions({ path: { assessment_uid: assessmentUid } });
}

export function impactOptions(assessmentUid: string) {
  return assessmentsGetVersionImpactOptions({ path: { assessment_uid: assessmentUid } });
}

export function topicOptions(assessmentUid: string, topicKey: string) {
  return assessmentsGetTopicResultOptions({ path: { assessment_uid: assessmentUid, topic_key: topicKey } });
}

export function rowOf(detail: TopicResultDetail): TopicResultRow {
  const level = ({ status, refers_to, label, tone, headline, confidence }: TopicResultDetail["wtg"]) => ({
    status,
    refers_to,
    label,
    tone,
    headline,
    confidence,
  });
  return {
    ordinal: detail.ordinal,
    key: detail.key,
    display_id: detail.display_id,
    group: detail.group,
    title: detail.title,
    clause_numbers: detail.clause_numbers,
    wtg: level(detail.wtg),
    ppc: level(detail.ppc),
    poc: level(detail.poc),
    review_state: detail.review_state,
    checks_count: detail.checks_count,
  };
}

export function storeTopic(queryClient: QueryClient, assessmentUid: string, detail: TopicResultDetail): void {
  queryClient.setQueryData(topicOptions(assessmentUid, detail.key).queryKey, detail);
  queryClient.setQueryData<TopicResultRow[]>(resultsOptions(assessmentUid).queryKey, (rows) =>
    rows?.map((row) => (row.key === detail.key ? rowOf(detail) : row)),
  );
}

const ASSESSMENT_READS = new Set([
  "assessmentsGetAssessment",
  "assessmentsListTopicResults",
  "assessmentsGetTopicResult",
  "assessmentsListAssessments",
  "capabilitiesGetCapabilityDocument",
  "capabilitiesListCapabilityDocuments",
]);

function operationOf(query: Query): string | null {
  const head = query.queryKey[0];
  return head !== null && typeof head === "object" && "_id" in head && typeof head._id === "string"
    ? head._id
    : null;
}

export async function invalidateAssessment(
  queryClient: QueryClient,
  options: { topics?: boolean } = {},
): Promise<void> {
  await queryClient.invalidateQueries({
    predicate: (query) => {
      const id = operationOf(query);
      if (id === null || !ASSESSMENT_READS.has(id)) {
        return false;
      }
      return options.topics === true || id !== "assessmentsGetTopicResult";
    },
  });
}
